import React, { Component } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { notification } from 'antd';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { pending } from 'redux-saga-thunk';
import IODetails from '../../components/pages/IODetails';
import {
  createValidator,
  email,
  maxArrayLength,
  required,
} from '../../services/validation';
import { fromEntities, fromResource } from '../../store/selectors';
import { resourceListReadRequest, resourceUpdateRequest } from '../../store/resource/actions';
import { modalHide, modalShow } from '../../store/modal/actions';
import { isOpen } from '../../store/modal/selectors';

class IODetailsContainer extends Component {
  componentDidMount() {
    this.getResults();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (get(this.props, 'campaign.id') && !get(prevProps, 'campaign.id')) {
      Promise.all([this.props.getVerticalList(this.props.campaign.advertiser_uuid)]).catch(e => {
        notification.error({
          message: 'Error',
          description: typeof e === 'string' ? e : 'Could not retrieve vertical data',
          duration: 0,
        });
      });
    }
  }

  getResults = () => {
    Promise.all([
      this.props.getAgencyBilling(),
      this.props.getVerticalList(this.props.campaign.advertiser_uuid),
      this.props.getAdminList(),
      this.props.getSalesList(),
      this.props.getAdvertiserList(),
      this.props.getPublisherList(),
    ]).catch(e => {
      notification.error({
        message: 'Error',
        description: typeof e === 'string' ? e : 'Could not retrieve backend data',
        duration: 0,
      });
    });
  };

  verticalOptions = () =>
    this.props.verticalList.map(a => ({
      title: a,
      label: a,
      value: a,
    }));

  advertiserOptions = () =>
    this.props.advertiserList.map(a => ({
      title: a.name,
      label: a.name,
      value: a.uuid,
    }));

  publisherOptions = () =>
    this.props.publisherList.map(a => ({
      title: a.name,
      label: a.name,
      value: a.uuid,
    }));

  adminOptions = () =>
    this.props.adminList.map(a => ({
      title: `${a.first_name} ${a.last_name} ${a.email}`,
      label: `${a.first_name} ${a.last_name} ${a.email}`,
      value: a.id,
    }));

  salesOptions = () =>
    this.props.salesList.map(a => ({
      title: `${a.first_name} ${a.last_name} ${a.email}`,
      label: `${a.first_name} ${a.last_name} ${a.email}`,
      value: a.id,
    }));

  render() {
    return (
      <IODetails
        verticalOptions={this.verticalOptions()}
        adminOptions={this.adminOptions()}
        salesOptions={this.salesOptions()}
        publisherOptions={this.publisherOptions()}
        advertiserOptions={this.advertiserOptions()}
        {...this.props}
      />
    );
  }
}

const onSubmit = (values, dispatch, props) => {
  const vals = values;
  const makeStrings = ['billing_customer', 'billing_email', 'agency'];
  makeStrings.forEach(v => {
    if (get(vals, v) && Array.isArray(vals[v])) {
      vals[v] = vals[v].shift();
    }
  });

  return props
    .updateCampaign(vals)
    .then(() => {
      notification.success({ message: 'Success', description: 'Campaign updated.', duration: 4 });
      props.hideModal();
    })
    .catch(e =>
      notification.error({
        message: 'Error',
        description: typeof e === 'string' ? e : 'Could not update campaign.',
        duration: 0,
      })
    );
};

const validate = createValidator({
  name: [required],
  actor_id: [required],
  agency: [maxArrayLength(1)],
  billing_customer: [maxArrayLength(1)],
  billing_email: [maxArrayLength(1), email],
  start_date: [required],
  end_date: [required],
  seller_pub_uuid: [required],
  advertiser_uuid: [required],
  io_value: [required],
});

const mapStateToProps = (
  { modal, updateCampaign, auth, ...state },
  {
    match: {
      params: { campaignId },
    },
  }
) => {
  const campaign = fromEntities.getDetail(state, 'advertiserCampaign', campaignId);

  return {
    campaign,
    loading: pending(state, 'advertiserCampaignUpdate'),
    adminName: get(auth, 'user.username', false),
    advertiserList: get(campaign, 'advertiser_uuid')
      ? fromEntities.getList(state, 'advertiser', fromResource.getList(state, 'advertiser'))
      : [],
    publisherList:
      fromEntities.getList(state, 'publisher', fromResource.getList(state, 'publisher')) || [],
    salesList: fromEntities.getList(state, 'userSales', fromResource.getList(state, 'userSales')),
    adminList: fromEntities.getList(state, 'userAdmin', fromResource.getList(state, 'userAdmin')),
    verticalList: fromResource.getList(state, 'advertiserVertical'),
    isEdit: !isEmpty(updateCampaign),
    initialValues: {
      seller_pub_uuid: '00EF4590-8F46-42F1-B69C-EDC954FFAD4B', // "inMarket"
      actor_id: `${get(auth, 'user.admin_id', false)}`,
      ...campaign,
      ...updateCampaign,
    },
    visibleModal: isOpen(modal, 'io-details'),
    agencies: get(
      fromResource.getList(state, 'advertiserCampaignAgencyBillingData'),
      'agencies',
      []
    ),
    billing_emails: get(
      fromResource.getList(state, 'advertiserCampaignAgencyBillingData'),
      'billing_emails',
      []
    ),
    billing_customers: get(
      fromResource.getList(state, 'advertiserCampaignAgencyBillingData'),
      'billing_customers',
      []
    ),
  };
};

const mapDispatchToProps = (
  dispatch,
  {
    match: {
      params: { campaignId },
    },
  }
) => ({
  getAgencyBilling: () =>
    dispatch(resourceListReadRequest('advertiser-campaign/agency-billing-data')),
  hideModal: () => dispatch(modalHide('io-details')),
  showModal: () => dispatch(modalShow('io-details')),
  getSalesList: () =>
    dispatch(resourceListReadRequest('user/sales', { sort_by: 'first_name', page_size: 50000 })),
  getVerticalList: advertiser_uuid => {
    return dispatch(
      resourceListReadRequest(
        'advertiser/:advertiser_uuid/vertical',
        { sort_by: 'vertical', page_size: 50000 },
        { advertiser_uuid }
      )
    );
  },
  getAdminList: () =>
    dispatch(resourceListReadRequest('user/admin', { sort_by: 'first_name', page_size: 50000 })),
  getAdvertiserList: () =>
    dispatch(resourceListReadRequest('advertiser', { sort_by: 'name', page_size: 50000 })),
  getPublisherList: () =>
    dispatch(resourceListReadRequest('publisher', { sort_by: 'name', page_size: 50000 })),
  updateCampaign: values =>
    dispatch(resourceUpdateRequest('advertiser-campaign/:id', { id: campaignId }, values)),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({
    enableReinitialize: true,
    destroyOnUnmount: false,
    form: 'CampaignIODetail',
    onSubmit,
    validate,
  })
)(IODetailsContainer);
